import React, { useMemo } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { usePmsWorkOrders, type PmsWorkOrder } from '../../pms/pms-work-orders/useData';
import { StatCounterGrid, type StatCounterItem } from './shared';

const CLOSED_STATUS_CODES = new Set(['COMPLETED', 'VERIFIED', 'CANCELLED', 'CLOSED']);

function statusCode(row: PmsWorkOrder): string {
  return String((row.status as { code?: string } | null | undefined)?.code ?? '').toUpperCase();
}

function isClosed(row: PmsWorkOrder): boolean {
  return CLOSED_STATUS_CODES.has(statusCode(row));
}

function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const PmsDueCounters: React.FC<{ embedded?: boolean; settings?: Record<string, unknown> }> = ({
  embedded,
}) => {
  const { colors } = useTheme();
  const { data, total, isLoading } = usePmsWorkOrders(undefined, 1, 200);
  const today = todayIso();

  const counters: StatCounterItem[] = useMemo(() => {
    const rows = data ?? [];
    let due = 0;
    let overdue = 0;
    let draft = 0;
    let planned = 0;
    let open = 0;
    let hasOverdueCode = false;

    for (const row of rows) {
      const code = statusCode(row);
      if (code === 'OVERDUE') hasOverdueCode = true;
      if (code === 'DUE') due += 1;
      else if (code === 'OVERDUE') overdue += 1;
      else if (code === 'DRAFT') draft += 1;
      else if (code === 'PLANNED') planned += 1;
      else if (code === 'OPEN' || code === 'IN_PROGRESS') open += 1;
    }

    if (!hasOverdueCode) {
      overdue = rows.filter((row) => {
        if (isClosed(row)) return false;
        const dueDate = row.plannedDueDate != null ? String(row.plannedDueDate).slice(0, 10) : '';
        return dueDate !== '' && dueDate < today;
      }).length;
    }

    const items: StatCounterItem[] = [
      {
        key: 'total',
        label: 'Total',
        value: total ?? rows.length,
        href: '/pms/pms-work-orders',
        accent: colors.primary,
      },
      {
        key: 'due',
        label: 'Due',
        value: due,
        href: '/pms/pms-work-orders',
        accent: 'bg-amber-500',
      },
      {
        key: 'overdue',
        label: 'Overdue',
        value: overdue,
        href: '/pms/pms-work-orders',
        accent: 'bg-rose-500',
      },
    ];

    if (draft > 0) {
      items.push({
        key: 'draft',
        label: 'Draft',
        value: draft,
        href: '/pms/pms-work-orders',
        accent: 'bg-slate-400',
      });
    }
    if (planned > 0) {
      items.push({
        key: 'planned',
        label: 'Planned',
        value: planned,
        href: '/pms/pms-work-orders',
        accent: colors.accent,
      });
    }
    if (open > 0) {
      items.push({
        key: 'open',
        label: 'Open',
        value: open,
        href: '/pms/pms-work-orders',
        accent: 'bg-sky-500',
      });
    }

    return items;
  }, [colors.accent, colors.primary, data, today, total]);

  return (
    <StatCounterGrid
      title="PMS · Work orders"
      counters={counters}
      isLoading={isLoading}
      embedded={embedded}
      showTotal={false}
      viewAllPath="/pms/pms-work-orders"
    />
  );
};

export default PmsDueCounters;
